// frontend/src/utils/notificationToastUtils.js
/**
 * Toast preview helpers for in-app notification polling.
 *
 * Digests (HR morning digest, RO leave digest, etc.) store multi-line
 * summaries in `message` for the inbox/email channels. Toasts must stay
 * a brief ping — title + CTA only — per the design system.
 */

const DIGEST_NOTIFICATION_TYPES = new Set([
  'hr_admin_digest',
  'leave_digest',
  'overtime_planning_digest',
  'rest_day_management_digest',
  'compliance_expiry_digest',
  'fleet_expiry_digest',
  'passport_expiry_digest',
]);

const MAX_TOAST_SUBTITLE_LENGTH = 90;

/**
 * @param {Object|null|undefined} notification
 * @returns {boolean}
 */
export const isDigestNotification = (notification) => {
  const type = notification?.notification_type || '';
  return DIGEST_NOTIFICATION_TYPES.has(type) || type.endsWith('_digest');
};

/**
 * Build concise toast copy from a notification record.
 *
 * @param {Object|null|undefined} notification
 * @returns {{ title: string, subtitle: string|null }}
 */
export const getNotificationToastPreview = (notification) => {
  const title = (notification?.title || 'New notification').trim();

  if (isDigestNotification(notification)) {
    return {
      title,
      subtitle: 'Tap to review',
    };
  }

  const rawMessage = (notification?.message || '').trim();
  const firstLine = rawMessage
    .split('\n')
    .map((line) => line.replace(/^•\s*/, '').trim())
    .find(Boolean);

  if (!firstLine) {
    return { title, subtitle: null };
  }

  const truncated =
    firstLine.length > MAX_TOAST_SUBTITLE_LENGTH
      ? `${firstLine.slice(0, MAX_TOAST_SUBTITLE_LENGTH - 1)}…`
      : firstLine;

  const titleLower = title.toLowerCase();
  const lineLower = truncated.toLowerCase();
  const isRedundant =
    lineLower === titleLower
    || lineLower.startsWith(titleLower)
    || titleLower.startsWith(lineLower);

  return {
    title,
    subtitle: isRedundant ? null : truncated,
  };
};
